import { Zap, Lock } from "lucide-react";
import { MATCH } from "@/lib/content";

/**
 * MatchChallenge banner.
 * - Locked state: hidden under a "Unlocks at $X" tease
 * - Active state: announces the match, shows progress of matched portion
 * - Completed state: celebrates when the pool is fully used
 */
export default function MatchChallenge({ stats }) {
  const raised = Number(stats?.total_raised || 0);
  const triggered = raised >= MATCH.trigger;
  const matched = Math.min(MATCH.pool, Math.max(0, raised - MATCH.trigger));
  const remaining = Math.max(0, MATCH.pool - matched);
  const pct = MATCH.pool > 0 ? Math.min(100, (matched / MATCH.pool) * 100) : 0;
  const completed = matched >= MATCH.pool;

  return (
    <section
      data-testid="match-challenge"
      className="max-w-7xl mx-auto px-6 md:px-12"
    >
      <div
        className={`relative overflow-hidden border ${
          completed
            ? "border-[#FACC15] bg-gradient-to-r from-[#FACC15]/10 via-[#FACC15]/5 to-transparent"
            : triggered
            ? "border-[#FF007F] bg-gradient-to-r from-[#FF007F]/15 via-[#FF007F]/5 to-transparent"
            : "border-[#27272A] bg-[#0B0B0D]"
        } p-6 md:p-8 mt-12`}
      >
        {/* Decorative corner stripe */}
        <div className="absolute top-0 right-0 h-1 w-32 diagonal-bar opacity-70" />

        <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div
            className={`h-12 w-12 grid place-items-center shrink-0 ${
              triggered ? "bg-[#FF007F]" : "bg-[#18181B] border border-[#27272A]"
            }`}
          >
            {triggered ? (
              <Zap className="h-6 w-6 text-white" />
            ) : (
              <Lock className="h-5 w-5 text-[#71717A]" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#FACC15]">
              Match Challenge
            </div>
            {!triggered && (
              <div data-testid="match-challenge-locked">
                <div className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-1 text-white">
                  Unlocks at ${MATCH.trigger.toLocaleString()} raised
                </div>
                <p className="text-[#A1A1AA] text-sm mt-2">
                  Once we hit ${MATCH.trigger.toLocaleString()}, {MATCH.sponsorName} will match
                  the next <span className="text-[#FF007F] font-semibold">${MATCH.pool.toLocaleString()}</span> in
                  donations — every dollar you give becomes two.
                </p>
              </div>
            )}
            {triggered && !completed && (
              <div data-testid="match-challenge-active">
                <div className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-1 text-white">
                  🔥 Active — next{" "}
                  <span className="text-[#FF007F]">${remaining.toFixed(0)}</span> is doubled
                </div>
                <p className="text-[#A1A1AA] text-sm mt-2">
                  {MATCH.sponsorName} is matching donations dollar-for-dollar up to
                  ${MATCH.pool.toLocaleString()}. Give now — your gift hits twice.
                </p>
              </div>
            )}
            {completed && (
              <div data-testid="match-challenge-completed">
                <div className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-1 text-[#FACC15]">
                  Full match achieved 🙌
                </div>
                <p className="text-[#A1A1AA] text-sm mt-2">
                  We unlocked the full ${MATCH.pool.toLocaleString()} match from {MATCH.sponsorName}.
                  Camden is one step closer to Mesa — keep going!
                </p>
              </div>
            )}

            {triggered && (
              <div className="mt-4">
                <div className="h-2 bg-[#27272A] overflow-hidden">
                  <div
                    data-testid="match-progress-bar"
                    className={`h-full ${completed ? "bg-[#FACC15]" : "bg-[#FF007F]"} transition-all duration-700`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-[10px] uppercase tracking-[0.25em] text-[#71717A] tabular">
                  <span>${matched.toFixed(0)} matched</span>
                  <span>${MATCH.pool.toLocaleString()} pool</span>
                </div>
              </div>
            )}
          </div>

          <a
            href="#donate"
            data-testid="match-challenge-cta"
            className={`shrink-0 inline-flex items-center justify-center px-6 py-3 font-bold uppercase tracking-[0.18em] text-sm transition-transform hover:-translate-y-0.5 ${
              triggered
                ? "bg-[#FF007F] text-white shadow-pink-glow"
                : "border border-[#27272A] text-white hover:border-white"
            }`}
          >
            {triggered ? "Double my gift" : "Be the spark"}
          </a>
        </div>
      </div>
    </section>
  );
}
